<template>
  <div class="d-inline-flex">
  <v-menu
    v-model="menuOpen"
    :close-on-content-click="false"
    content-class="follow-up-menu elevation-8"
    max-width="460"
    min-width="420"
    offset-y
    bottom
    left
  >
    <template #activator="{ on, attrs }">
      <v-tooltip bottom :disabled="menuOpen">
        <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
          <v-btn
            class="mx-1"
            icon
            :aria-label="`Follow-Up Actions: ${activeCount} active, ${incomingCount} incoming`"
            v-bind="{ ...attrs, ...tooltipAttrs }"
            v-on="{ ...on, ...tooltipOn }"
          >
            <v-badge
              :content="String(headerBadge.content)"
              :value="headerBadge.visible"
              :color="headerBadge.color"
              data-cy="follow-up-header-badge"
              overlap
            >
              <v-icon>mdi-clipboard-check-outline</v-icon>
            </v-badge>
          </v-btn>
        </template>
        <span>Follow-Up Actions</span>
      </v-tooltip>
    </template>

    <v-card class="rounded-lg overflow-hidden" width="460">
      <v-card-title class="px-5 py-4">
        <div class="flex-grow-1 min-width-0">
          <div class="d-flex align-center">
            <div class="subtitle-1 font-weight-bold">Follow-Up Actions</div>
            <v-spacer />
            <v-btn
              icon
              small
              color="primary"
              :loading="loading"
              aria-label="Refresh Follow-Up Actions"
              title="Refresh"
              @click="load"
            >
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
          </div>
          <div class="d-flex align-center mt-1">
            <span class="caption grey--text text--darken-1 font-weight-regular">{{ menuSubtitle }}</span>
            <v-spacer />
            <v-btn text x-small color="primary" class="text-capitalize px-1" @click="openManage()">
              Manage Actions
            </v-btn>
          </div>
        </div>
      </v-card-title>

      <v-divider />

      <div v-if="incomingCount" class="d-flex align-center justify-space-between px-5 py-3 incoming-shortcut">
        <span class="body-2">New assignments to review</span>
        <v-chip color="orange darken-2" dark label small clickable class="font-weight-medium" @click="openManage(null, 'incoming')" data-cy="follow-up-incoming-shortcut" :aria-label="`Open Incoming actions, ${incomingCount} requests`">
          <v-icon small left>mdi-inbox-arrow-down-outline</v-icon>
          {{ incomingCount }} Incoming
          <v-icon small right>mdi-arrow-right</v-icon>
        </v-chip>
      </div>

      <template v-if="showFilters">
        <div class="d-flex align-center px-4 py-2 filter-toolbar">
          <v-btn
            text
            small
            color="primary"
            class="text-capitalize px-2"
            @click="filtersExpanded = !filtersExpanded"
          >
            <v-icon small left>mdi-filter-variant</v-icon>
            Filters
            <v-badge
              v-if="activeFilterCount"
              :content="activeFilterCount"
              color="primary"
              inline
              class="ml-1"
            />
            <v-icon small right>{{ filtersExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </v-btn>
          <v-spacer />
          <span class="caption grey--text text--darken-1 mr-1">
            {{ filteredActions.length }} {{ filteredActions.length === 1 ? 'result' : 'results' }} found
          </span>
        </div>

        <v-expand-transition>
          <div v-show="filtersExpanded" class="filters-panel px-4 pt-3 pb-4">
            <v-select
              v-model="filters.module"
              :items="moduleOptions"
              label="Module"
              placeholder="All"
              persistent-placeholder
              multiple
              small-chips
              deletable-chips
              clearable
              dense
              outlined
              hide-details
              class="mb-3"
              @change="normalizeMultiFilter('module', $event)"
            >
              <template #selection="{ item, index }">
                <v-chip v-if="index === 0" x-small>
                  {{ filters.module.includes('all') ? 'All' : item.text }}
                </v-chip>
                <span
                  v-else-if="index === 1 && !filters.module.includes('all')"
                  class="caption grey--text ml-1"
                >
                  +{{ filters.module.length - 1 }}
                </span>
              </template>
            </v-select>
            <v-select
              v-model="filters.actionType"
              :items="actionTypeOptions"
              label="Action Type"
              placeholder="All"
              persistent-placeholder
              multiple
              small-chips
              deletable-chips
              clearable
              dense
              outlined
              hide-details
              class="mb-3"
              @change="normalizeMultiFilter('actionType', $event)"
            >
              <template #selection="{ item, index }">
                <v-chip v-if="index === 0" x-small>
                  {{ filters.actionType.includes('all') ? 'All' : item.text }}
                </v-chip>
                <span
                  v-else-if="index === 1 && !filters.actionType.includes('all')"
                  class="caption grey--text ml-1"
                >
                  +{{ filters.actionType.length - 1 }}
                </span>
              </template>
            </v-select>
            <v-row dense>
              <v-col cols="6">
                <v-select
                  v-model="filters.status"
                  :items="statusOptions"
                  label="Status"
                  placeholder="All"
                  persistent-placeholder
                  multiple
                  small-chips
                  deletable-chips
                  clearable
                  dense
                  outlined
                  hide-details
                  @change="normalizeMultiFilter('status', $event)"
                >
                  <template #selection="{ item, index }">
                    <v-chip v-if="index === 0" x-small>
                      {{ filters.status.includes('all') ? 'All' : item.text }}
                    </v-chip>
                    <span
                      v-else-if="index === 1 && !filters.status.includes('all')"
                      class="caption grey--text ml-1"
                    >
                      +{{ filters.status.length - 1 }}
                    </span>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="filters.priority"
                  :items="priorityFilterOptions"
                  label="Priority"
                  placeholder="All"
                  persistent-placeholder
                  multiple
                  small-chips
                  deletable-chips
                  clearable
                  dense
                  outlined
                  hide-details
                  @change="normalizeMultiFilter('priority', $event)"
                >
                  <template #selection="{ item, index }">
                    <v-chip v-if="index === 0" x-small>
                      {{ filters.priority.includes('all') ? 'All' : item.text }}
                    </v-chip>
                    <span
                      v-else-if="index === 1 && !filters.priority.includes('all')"
                      class="caption grey--text ml-1"
                    >
                      +{{ filters.priority.length - 1 }}
                    </span>
                  </template>
                </v-select>
              </v-col>
            </v-row>
            <v-select
              v-model="filters.dueDate"
              :items="dueDateOptions"
              label="Due Date"
              placeholder="All"
              persistent-placeholder
              multiple
              small-chips
              deletable-chips
              clearable
              dense
              outlined
              hide-details
              class="mt-3"
              @change="normalizeMultiFilter('dueDate', $event)"
            >
              <template #selection="{ item, index }">
                <v-chip v-if="index === 0" x-small>
                  {{ filters.dueDate.includes('all') ? 'All' : item.text }}
                </v-chip>
                <span
                  v-else-if="index === 1 && !filters.dueDate.includes('all')"
                  class="caption grey--text ml-1"
                >
                  +{{ filters.dueDate.length - 1 }}
                </span>
              </template>
            </v-select>
            <v-row
              v-if="filters.dueDate.includes('custom') && !filters.dueDate.includes('all')"
              dense
              class="mt-1"
            >
              <v-col cols="6">
                <v-text-field
                  v-model="filters.dueFrom"
                  type="date"
                  label="From"
                  dense
                  outlined
                  hide-details
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="filters.dueTo"
                  type="date"
                  label="To"
                  dense
                  outlined
                  hide-details
                />
              </v-col>
            </v-row>
            <div class="d-flex justify-end mt-3">
              <v-btn
                text
                small
                color="primary"
                class="text-capitalize"
                :disabled="!activeFilterCount"
                @click="clearFilters"
              >
                Clear Filters
              </v-btn>
            </div>
          </div>
        </v-expand-transition>
        <v-divider />
      </template>

      <div v-if="loading && !actions.length" class="text-center py-12">
        <v-progress-circular indeterminate color="primary" size="32" width="3" />
        <div class="caption grey--text mt-3">Loading assigned actions...</div>
      </div>

      <v-alert v-else-if="error" type="error" text dense class="ma-4">
        {{ error }}
        <v-btn text small color="error" class="text-capitalize ml-1" @click="load">
          Try again
        </v-btn>
      </v-alert>

      <div v-else-if="!actions.length" class="text-center px-6 py-12">
        <v-avatar color="primary lighten-5" size="52">
          <v-icon color="primary">mdi-clipboard-check-outline</v-icon>
        </v-avatar>
        <div class="subtitle-2 font-weight-medium mt-3">No follow-up actions</div>
        <div class="caption grey--text mt-1">Your assigned actions will appear here.</div>
      </div>

      <div v-else-if="!filteredActions.length" class="text-center px-6 py-10">
        <v-avatar color="blue-grey lighten-5" size="48">
          <v-icon color="blue-grey">mdi-filter-off-outline</v-icon>
        </v-avatar>
        <div class="subtitle-2 font-weight-medium mt-3">No matching actions</div>
        <div class="caption grey--text mt-1">Try changing or clearing your filters.</div>
        <v-btn text small color="primary" class="text-capitalize mt-2" @click="clearFilters">
          Clear Filters
        </v-btn>
      </div>

      <div
        v-else
        ref="actionList"
        class="action-list pa-3"
        :class="{ 'filters-open': filtersExpanded }"
        @scroll.passive="handleResultsScroll"
      >
        <v-card
          v-for="action in visibleActions"
          :key="action.id"
          outlined
          class="action-card mb-3 rounded-lg"
        >
          <div class="pa-4">
            <div class="action-heading">
              <div class="body-2 font-weight-bold text-truncate">
                {{ action.actionType.name }}
              </div>
              <div
                v-if="action.actionType.description"
                class="caption grey--text text--darken-1 action-description mt-1"
                :class="{
                  'action-description-collapsed':
                    isLongDescription(action) && !isDescriptionExpanded(action.id)
                }"
              >
                {{ action.actionType.description }}
              </div>
              <v-btn
                v-if="isLongDescription(action)"
                text
                x-small
                color="primary"
                class="description-toggle text-capitalize mt-1"
                :aria-expanded="isDescriptionExpanded(action.id).toString()"
                @click.stop="toggleDescription(action.id)"
              >
                {{ isDescriptionExpanded(action.id) ? 'Read Less' : 'Read More' }}
                <v-icon x-small right>
                  {{ isDescriptionExpanded(action.id) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </v-btn>
            </div>

            <v-chip
              x-small
              label
              dark
              :color="priorityColor(action.priority.id)"
              class="action-priority"
            >
              {{ action.priority.name }}
            </v-chip>

            <div class="action-details mt-4">
              <div class="detail-item">
                <v-avatar size="28" color="blue-grey lighten-5" class="mr-2">
                  <v-icon small color="blue-grey darken-1">mdi-link-variant</v-icon>
                </v-avatar>
                <div class="min-width-0">
                  <div class="detail-label">Related Record</div>
                  <div class="detail-value text-truncate">
                    {{ relatedRecordLabel(action) }}
                  </div>
                </div>
              </div>

              <div class="detail-item">
                <v-avatar size="28" color="blue-grey lighten-5" class="mr-2">
                  <v-icon small color="blue-grey darken-1">mdi-apps</v-icon>
                </v-avatar>
                <div class="min-width-0">
                  <div class="detail-label">App</div>
                  <div class="detail-value action-modules">
                    <span class="text-truncate">{{ moduleName(action.moduleIds?.[0] || action.module) }}</span>
                    <v-tooltip v-if="otherActionModules(action).length" bottom>
                      <template #activator="{ on, attrs }">
                        <span class="other-modules ml-1" v-bind="attrs" v-on="on">
                          (+{{ otherActionModules(action).length }} more)
                        </span>
                      </template>
                      <div v-for="moduleId in otherActionModules(action)" :key="moduleId">
                        {{ moduleName(moduleId) }}
                      </div>
                    </v-tooltip>
                  </div>
                </div>
              </div>

              <div class="detail-item">
                <v-avatar size="28" color="blue-grey lighten-5" class="mr-2">
                  <v-icon small color="blue-grey darken-1">mdi-progress-check</v-icon>
                </v-avatar>
                <div>
                  <div class="detail-label">Status</div>
                  <v-chip x-small label outlined :color="statusColor(action.status)" class="mt-1">
                    {{ statusName(action.status) }}
                  </v-chip>
                </div>
              </div>

              <div class="detail-item">
                <v-avatar
                  size="28"
                  :color="isOverdue(action) ? 'red lighten-5' : 'blue-grey lighten-5'"
                  class="mr-2"
                >
                  <v-icon small :color="isOverdue(action) ? 'error' : 'blue-grey darken-1'">
                    mdi-calendar-clock-outline
                  </v-icon>
                </v-avatar>
                <div>
                  <div class="detail-label">Due</div>
                  <div
                    class="detail-value"
                    :class="{ 'error--text font-weight-medium': isOverdue(action) }"
                  >
                    {{ dueLabel(action) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <v-divider />
          <div class="d-flex align-center px-4 py-2 action-footer">
            <span class="caption grey--text text--darken-1">
              <v-icon x-small color="grey darken-1" class="mr-1">mdi-comment-text-outline</v-icon>
              {{ action.comments.length }}
              {{ action.comments.length === 1 ? 'comment' : 'comments' }}
            </span>
            <v-spacer />
            <v-btn
              text
              x-small
              color="primary"
              class="text-capitalize view-action-button"
              :loading="openingActionId === action.id"
              :disabled="Boolean(openingActionId)"
              @click="openAction(action)"
            >
              View Details
              <v-icon x-small right>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </v-card>
        <div
          v-if="hasMoreActions"
          class="caption grey--text text--darken-1 text-center pb-1"
        >
          Scroll to load more
        </div>
      </div>

      <template v-if="actions.length">
        <v-divider />
        <v-card-text class="caption grey--text text--darken-1 d-flex align-center justify-center py-3">
          You have
          <v-badge
            :content="activeCount"
            :value="true"
            color="primary"
            inline
            class="mx-1"
          />
          active assigned {{ activeCount === 1 ? 'action' : 'actions' }}
        </v-card-text>
      </template>
    </v-card>
  </v-menu>
  <v-dialog v-model="manageDialog" fullscreen hide-overlay>
    <FollowUpActionsPanel
      v-if="manageDialog"
      global-mode
      :focus-action-id="manageActionId"
      :initial-queue="manageQueue"
      @close="manageDialog = false"
      @view-details="openAction"
    />
  </v-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { mapGetters } from 'vuex';
import { EventBus } from '@/event-bus';
import { FOLLOW_UP_ACTIONS_CHANGED } from '@/lib/constants/followUpActions';
import CoreMixin from '@/mixins/core.mixin';
import { CaseManagementService } from '@/modules/delinquency/services/case-management.service';
import { FollowUpActionsService } from '@/modules/shared/services/follow-up-actions.service';
import FollowUpActionsPanel from '@/modules/shared/components/follow-up-actions/FollowUpActionsPanel.vue';
import { actionBadge } from './assignment-utils';

const service = new FollowUpActionsService();
const caseService = new CaseManagementService();

export default {
  name: 'FollowUpActionsMenu',
  components: { FollowUpActionsPanel },
  mixins: [CoreMixin],
  data: () => ({
    menuOpen: false,
    manageDialog: false,
    manageActionId: null,
    manageQueue: 'active',
    incomingCount: 0,
    incomingActions: [],
    rejectedActions: [],
    refreshTimer: null,
    lastSummaryKey: null,
    destroyed: false,
    loading: false,
    refreshQueued: false,
    openingActionId: null,
    error: '',
    actions: [],
    expandedDescriptions: {},
    activeCount: 0,
    visibleActionLimit: 5,
    actionPageSize: 5,
    options: { modules: [], actionTypes: [], priorities: [] },
    filtersExpanded: false,
    filters: {
      module: [],
      actionType: [],
      status: ['pending', 'in_progress'],
      priority: [],
      dueDate: [],
      dueFrom: '',
      dueTo: ''
    },
    filterSnapshots: {
      module: [],
      actionType: [],
      status: ['pending', 'in_progress'],
      priority: [],
      dueDate: []
    },
    statusOptions: [
      { text: 'All', value: 'all' },
      { text: 'Pending', value: 'pending' },
      { text: 'In Progress', value: 'in_progress' },
      { text: 'Completed', value: 'completed' },
      { text: 'Cancelled', value: 'cancelled' }
    ],
    dueDateOptions: [
      { text: 'All', value: 'all' },
      { text: 'Today', value: 'today' },
      { text: 'Tomorrow', value: 'tomorrow' },
      { text: 'This Week', value: 'this_week' },
      { text: 'Overdue', value: 'overdue' },
      { text: 'Custom Date Range', value: 'custom' }
    ]
  }),
  computed: {
    headerBadge() { return actionBadge(this.activeCount, this.incomingCount); },
    ...mapGetters('appModulesModule', ['activeModuleNames']),
    showFilters() {
      return true;
    },
    menuSubtitle() {
      return 'Actions assigned to you';
    },
    moduleOptions() {
      return [
        { text: 'All', value: 'all' },
        ...this.options.modules.map(module => ({ text: this.moduleName(module), value: module.id }))
      ];
    },
    actionTypeOptions() {
      return [
        { text: 'All', value: 'all' },
        ...this.options.actionTypes.map(actionType => ({
          text: actionType.name,
          value: actionType.id
        }))
      ];
    },
    priorityFilterOptions() {
      return [
        { text: 'All', value: 'all' },
        ...this.options.priorities.map(priority => ({ text: priority.name, value: priority.id }))
      ];
    },
    filteredActions() {
      return this.actions.filter(action => {
        if (
          this.filters.module.length &&
          !this.filters.module.includes('all') &&
          !this.filters.module.some(moduleId =>
            (moduleId === 'Global' && action.relatedRecordType === 'global') ||
            (action.moduleIds?.length ? action.moduleIds : [action.module]).includes(moduleId)
          )
        ) return false;
        if (
          this.filters.actionType.length &&
          !this.filters.actionType.includes('all') &&
          !this.filters.actionType.includes(action.actionType.id)
        ) return false;
        if (
          this.filters.status.length &&
          !this.filters.status.includes('all') &&
          !this.filters.status.includes(action.status)
        ) return false;
        if (
          this.filters.priority.length &&
          !this.filters.priority.includes('all') &&
          !this.filters.priority.includes(action.priority.id)
        ) return false;
        return this.matchesDueDate(action);
      });
    },
    visibleActions() {
      return this.filteredActions.slice(0, this.visibleActionLimit);
    },
    hasMoreActions() {
      return this.visibleActions.length < this.filteredActions.length;
    },
    activeFilterCount() {
      return ['module', 'actionType', 'status', 'priority', 'dueDate'].filter(
        key => this.filters[key].length > 0 && !this.filters[key].includes('all')
      ).length;
    }
  },
  watch: {
    'user.id'() {
      this.activeCount = 0; this.incomingCount = 0; this.actions = []; this.incomingActions = []; this.rejectedActions = []; this.lastSummaryKey = null;
      this.load();
    },
    menuOpen(open) {
      if (open) this.load();
    },
    filters: {
      deep: true,
      handler() {
        this.resetResultsPagination();
      }
    }
  },
  mounted() {
    EventBus.$on(FOLLOW_UP_ACTIONS_CHANGED, this.handleActionsChanged);
    document.addEventListener('visibilitychange', this.refreshVisibleActions);
    this.refreshTimer = setInterval(this.refreshVisibleActions, 30000);
    this.load();
  },
  beforeDestroy() {
    this.destroyed = true;
    clearInterval(this.refreshTimer);
    document.removeEventListener('visibilitychange', this.refreshVisibleActions);
    EventBus.$off(FOLLOW_UP_ACTIONS_CHANGED, this.handleActionsChanged);
  },
  methods: {
    openManage(actionId = null, queue = 'active') {
      this.menuOpen = false;
      this.manageActionId = actionId;
      this.manageQueue = queue;
      this.manageDialog = true;
    },
    refreshVisibleActions() { if (document.visibilityState === 'visible') this.load(); },
    async load() {
      if (!this.user?.id || this.destroyed) return;
      if (this.loading) {
        this.refreshQueued = true;
        return;
      }
      this.loading = true;
      const userId = this.user.id;
      this.error = '';
      try {
        const { data } = await service.getAssigned();
        if (this.destroyed || userId !== this.user?.id) return;
        const previousIncomingIds = new Set(this.incomingActions.map(action => action.id));
        const loadedBefore = this.lastSummaryKey !== null;
        this.incomingActions = data.incomingActions || [];
        this.incomingCount = data.incomingCount || 0;
        this.rejectedActions = data.rejectedActions || [];
        this.actions = [...data.actions].sort(
          (left, right) => dayjs(right.dateCreated).valueOf() - dayjs(left.dateCreated).valueOf()
        );
        this.activeCount = data.activeCount;
        this.options = data.options;
        const summaryKey = JSON.stringify([...this.actions, ...this.incomingActions, ...this.rejectedActions, ...(data.sentActions || [])].map(action => [action.id, action.status, action.assignmentStatus, action.assignmentVersion, action.dateUpdated, action.comments.length]));
        if (loadedBefore && summaryKey !== this.lastSummaryKey) EventBus.$emit(FOLLOW_UP_ACTIONS_CHANGED, { source: this._uid });
        this.lastSummaryKey = summaryKey;
        const newRequests = this.incomingActions.filter(action => !previousIncomingIds.has(action.id));
        if (loadedBefore && newRequests.length && document.visibilityState === 'visible') this.showSnack({ color: 'warning', title: 'Incoming Follow-Up Actions', message: `${newRequests.length} new ${newRequests.length === 1 ? 'assignment is' : 'assignments are'} waiting for your response.` });
        this.resetResultsPagination();
      } catch (error) {
        this.error = 'Unable to load your follow-up actions.';
      } finally {
        this.loading = false;
        if (this.refreshQueued && !this.destroyed) {
          this.refreshQueued = false;
          this.load();
        }
      }
    },
    handleActionsChanged(event) {
      if (event?.source !== this._uid) this.load();
    },
    isOverdue(action) {
      return ['pending', 'in_progress'].includes(action.status) && dayjs(action.dueAt).isBefore(dayjs());
    },
    dueLabel(action) {
      const dueAt = dayjs(action.dueAt);
      const date = dueAt.format('MMM D, YYYY [at] h:mm A');
      return this.isOverdue(action) ? `Overdue · ${date}` : date;
    },
    async openAction(action) {
      if (this.openingActionId) return;
      if (action.relatedRecordType === 'global') {
        this.openManage(action.id, ['completed', 'cancelled'].includes(action.status) ? 'history' : 'active');
        return;
      }
      this.openingActionId = action.id;
      const destination = {
        name: 'my-assigned-cases',
        query: {
          id: action.relatedRecordId,
          tab: 'follow-up-actions',
          followUpAction: action.id,
          openFollowUp: Date.now().toString()
        }
      };
      try {
        if (action.relatedRecordType === 'delinquency_case') {
          const { data: caseFile } = await caseService.getCase(action.relatedRecordId);
          if (String(caseFile?.status).toLowerCase() === 'closed') {
            this.menuOpen = false;
            this.manageDialog = false;
            this.showSnack({
              color: 'warning',
              title: 'Case Closed',
              message: 'This case is closed and cannot be viewed.'
            });
            return;
          }
        }
        this.menuOpen = false;
        this.manageDialog = false;
        await this.$router.push(destination);
      } catch (error) {
        if (error?.name === 'NavigationDuplicated') return;
        this.showSnack({
          color: 'error',
          title: 'Unable to Open',
          message: 'The related record could not be verified or opened. Please try again.'
        });
      } finally {
        this.openingActionId = null;
      }
    },
    relatedRecordLabel(action) {
      if (action.relatedRecordType === 'global') return 'None';
      if (action.relatedRecordType === 'delinquency_case') return 'Delinquency Case';
      return action.relatedRecordLabel || 'Related Record';
    },
    resetResultsPagination() {
      this.visibleActionLimit = this.actionPageSize;
      this.$nextTick(() => {
        if (this.$refs.actionList) this.$refs.actionList.scrollTop = 0;
      });
    },
    handleResultsScroll(event) {
      if (!this.hasMoreActions) return;
      const list = event.currentTarget;
      const distanceFromBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
      if (distanceFromBottom <= 80) {
        this.visibleActionLimit += this.actionPageSize;
      }
    },
    isLongDescription(action) {
      return (action.actionType.description || '').trim().length > 100;
    },
    isDescriptionExpanded(actionId) {
      return Boolean(this.expandedDescriptions[actionId]);
    },
    toggleDescription(actionId) {
      this.$set(
        this.expandedDescriptions,
        actionId,
        !this.isDescriptionExpanded(actionId)
      );
    },
    priorityColor(priority) {
      return {
        low: 'blue-grey',
        medium: 'amber darken-2',
        high: 'deep-orange',
        urgent: 'red darken-2'
      }[priority] || 'grey';
    },
    moduleName(module) {
      const id = typeof module === 'string' ? module : module?.id;
      const configuredName = typeof module === 'string' ? '' : module?.name;
      const normalizedId = this.normalizeModuleName(id);
      const activeName = this.activeModuleNames.find(
        name => this.normalizeModuleName(name) === normalizedId
      );
      const apiModule = this.options.modules.find(
        candidate => this.normalizeModuleName(candidate.id) === normalizedId
      );
      return activeName || apiModule?.name || configuredName || id || 'Unknown App';
    },
    otherActionModules(action) {
      const modules = action.moduleIds?.length ? action.moduleIds : [action.module];
      return [...new Set(modules.slice(1))];
    },
    normalizeModuleName(value) {
      return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    },
    matchesDueDate(action) {
      const filters = this.filters.dueDate;
      if (!filters.length || filters.includes('all')) return true;
      const dueAt = dayjs(action.dueAt);
      const today = dayjs();
      return filters.some(filter => {
        if (filter === 'overdue') return this.isOverdue(action);
        if (filter === 'today') return dueAt.isSame(today, 'day');
        if (filter === 'tomorrow') return dueAt.isSame(today.add(1, 'day'), 'day');
        if (filter === 'this_week') {
          return !dueAt.isBefore(today.startOf('week')) && !dueAt.isAfter(today.endOf('week'));
        }
        if (filter !== 'custom') return false;
        if (this.filters.dueFrom && dueAt.isBefore(dayjs(this.filters.dueFrom).startOf('day'))) return false;
        if (this.filters.dueTo && dueAt.isAfter(dayjs(this.filters.dueTo).endOf('day'))) return false;
        return true;
      });
    },
    normalizeMultiFilter(key, values) {
      const selected = Array.isArray(values) ? values : [];
      const previous = this.filterSnapshots[key] || [];
      const available = this.filterOptionValues(key);
      const hasAll = selected.includes('all');
      const hadAll = previous.includes('all');
      let normalized;

      if (hasAll && !hadAll) {
        normalized = ['all', ...available];
      } else if (hasAll && hadAll && selected.length < previous.length) {
        normalized = selected.filter(value => value !== 'all');
      } else if (!hasAll && hadAll) {
        normalized = [];
      } else {
        const selectedOptions = selected.filter(value => value !== 'all');
        normalized = available.length && selectedOptions.length === available.length
          ? ['all', ...available]
          : selectedOptions;
      }

      this.setFilterSelection(key, normalized);
    },
    filterOptionValues(key) {
      const options = {
        module: this.moduleOptions,
        actionType: this.actionTypeOptions,
        status: this.statusOptions,
        priority: this.priorityFilterOptions,
        dueDate: this.dueDateOptions
      }[key] || [];
      return options.map(option => option.value).filter(value => value !== 'all');
    },
    setFilterSelection(key, values) {
      const selection = [...new Set(values)];
      this.filters[key] = selection;
      this.filterSnapshots[key] = [...selection];
    },
    clearFilters() {
      this.filters = {
        module: [],
        actionType: [],
        status: [],
        priority: [],
        dueDate: [],
        dueFrom: '',
        dueTo: ''
      };
      this.filterSnapshots = {
        module: [],
        actionType: [],
        status: [],
        priority: [],
        dueDate: []
      };
    },
    statusName(status) {
      return {
        pending: 'Pending',
        in_progress: 'In Progress',
        completed: 'Completed',
        cancelled: 'Cancelled'
      }[status] || status;
    },
    statusColor(status) {
      return {
        pending: 'blue-grey',
        in_progress: 'primary',
        completed: 'success',
        cancelled: 'grey darken-1'
      }[status] || 'grey';
    }
  }
};
</script>

<style scoped>
.follow-up-menu {
  border: 1px solid #e4e7ec;
}
.filter-toolbar {
  min-height: 44px;
  background: #fff;
}
.filters-panel {
  background: #f8fafc;
}
.action-list {
  max-height: 420px;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: #f8fafc;
}
.action-list.filters-open {
  max-height: 240px;
}
.action-card {
  border-color: #e4e7ec !important;
  position: relative;
}
.action-card:last-child {
  margin-bottom: 0 !important;
}
.min-width-0 {
  min-width: 0;
}
.action-heading {
  padding-right: 82px;
}
.action-priority {
  position: absolute;
  right: 16px;
  top: 14px;
}
.action-description {
  line-height: 1.35;
  white-space: normal;
}
.action-description-collapsed {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}
.description-toggle {
  height: auto !important;
  letter-spacing: 0;
  min-width: 0 !important;
  padding: 0 !important;
}
.action-details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 18px;
}
.detail-item {
  display: flex;
  align-items: flex-start;
  min-width: 0;
}
.detail-label {
  color: #98a2b3;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1.2;
  text-transform: uppercase;
}
.detail-value {
  color: #475467;
  font-size: 12px;
  line-height: 1.35;
  margin-top: 3px;
}
.action-modules {
  display: flex;
  align-items: center;
  min-width: 0;
}
.action-modules .text-truncate {
  min-width: 0;
}
.other-modules {
  color: #1976d2;
  cursor: help;
  flex: 0 0 auto;
  font-weight: 600;
  white-space: nowrap;
}
.action-footer {
  min-height: 42px;
  background: #fff;
}
.view-action-button {
  flex: 0 0 auto;
}
</style>
